import { useState } from "react";
import { Box, IconButton, Popover, Stack, TextField, Tooltip } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { HexColorPicker } from "react-colorful";

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

interface IColorFieldProps {
  label: string;
  value: string;
  isOverridden: boolean;
  defaultValueLabel: string;
  helperText?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

export const ColorField = ({
  label,
  value,
  isOverridden,
  defaultValueLabel,
  helperText,
  disabled,
  onChange,
  onReset,
}: IColorFieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const pickerValue = HEX_PATTERN.test(value) ? value : "#000000";

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
      <Tooltip title={disabled ? "Not editable" : "Pick a color"}>
        <span>
          <Box
            component="button"
            type="button"
            disabled={disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.23)",
              backgroundColor: value,
              cursor: disabled ? "default" : "pointer",
              backgroundImage:
                value === "transparent"
                  ? "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)"
                  : undefined,
              backgroundSize: "8px 8px",
              backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
              flexShrink: 0,
            }}
          />
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5 }}>
          <HexColorPicker
            color={pickerValue}
            onChange={(color) => onChange(color)}
          />
        </Box>
      </Popover>
      <TextField
        size="small"
        label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        helperText={
          disabled
            ? helperText
            : (helperText ? `${helperText} — ` : "") +
              (isOverridden ? "Custom value" : `Default: ${defaultValueLabel}`)
        }
        sx={{ flex: 1 }}
      />
      <Tooltip title="Reset to default">
        <span>
          <IconButton
            size="small"
            onClick={onReset}
            disabled={disabled || !isOverridden}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};
